import {Action, Mutation, VuexModule} from 'vuex-module-decorators';
import {ILogger} from '@/utils/ILogger';

import {LoraForwarderState} from '@/model/lora/LoraForwarderState';
import {JobOutputMessage} from '@/model/lora/JobOutputMessage';
import {LoraPacket} from '@/model/lora/LoraPacket/LoraPacket';
import {Event} from '@/model/Event';

import {IEventsService} from '@/services/IEvents.service';
import {ILoraForwarderStdService} from '@/services/lora/forwarder/ILoraForwarderStd.service';

export abstract class LoraForwarderStore extends VuexModule {
  protected abstract logger: ILogger;
  protected abstract forwarderService: ILoraForwarderStdService;
  protected abstract eventsService: IEventsService;

  protected enabled: boolean | null = null;

  protected forwarderState: LoraForwarderState = LoraForwarderState.Unknown;
  protected stateSubscriptionId: number | null = null;

  protected logs: JobOutputMessage[] = [];
  protected logsMaxSize: number = 1000;
  protected logSubscriptionId: number | null = null;

  protected packets: LoraPacket[] = [];
  protected packetsSubscriptionId: number | null = null;
  protected packetsMaxSize: number = 200;

  @Mutation
  public clear() {
    this.enabled = null;
    this.forwarderState = LoraForwarderState.Unknown;
    this.logs = [];
  }

  /*  Enabled */
  @Action({commit: 'setEnabled', rawError: true})
  public async fetchEnabled() {
    return await this.forwarderService.getEnabled();
  }

  @Mutation
  public setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  public get Enabled(): boolean | null {
    return this.enabled;
  }

  /* State */
  @Action({commit: 'setState', rawError: true})
  public async fetchState() {
    return await this.forwarderService.getState();
  }

  @Action({commit: 'setStateSubscriptionId', rawError: true})
  public async subscribeState() {
    return await this.eventsService.subscribe('lora.forwarder.state', async (event: Event) => {
      this.context.commit('setState', event.Data);
    });
  }

  @Action({rawError: true})
  public async unsubscribeState() {
    if (this.stateSubscriptionId !== null) {
      await this.eventsService.unsubscribe(this.stateSubscriptionId);
    }
  }

  @Mutation
  public setState(state: LoraForwarderState) {
    this.forwarderState = state;
  }

  @Mutation
  public setStateSubscriptionId(id: number) {
    this.stateSubscriptionId = id;
  }

  public get State(): LoraForwarderState {
    return this.forwarderState;
  }

  /* Logs */
  @Action({commit: 'setLogSubscriptionId', rawError: true})
  public async subscribeLogs() {
    return await this.eventsService.subscribe('lora.forwarder.output', async (event: Event) => {
      this.context.commit('addLog', JobOutputMessage.Serializer.parse(event.Data));
    });
  }

  @Action({rawError: true})
  public async unsubscribeLogs() {
    if (this.logSubscriptionId !== null) {
      await this.eventsService.unsubscribe(this.logSubscriptionId);
    }
  }

  @Mutation
  public setLogs(logs: JobOutputMessage[]) {
    this.logs = logs;
  }

  @Mutation
  public addLog(log: JobOutputMessage) {

    // const jobLogs = this.logs.filter((m) => m.Job === log.Job);
    //
    // if (log.Id === 0 || jobLogs.length === 0) {
    //   this.logs.push(log);
    //   return;
    // }
    //
    // let currentPosition = jobLogs.length - 1;
    //
    // while (log.Id < jobLogs[currentPosition].Id) {
    //   currentPosition--;
    //   if (currentPosition < 0 || currentPosition < jobLogs.length - 10) {
    //     currentPosition = jobLogs.length - 1;
    //     break;
    //   }
    // }
    //
    // const insertPosition = this.logs.indexOf(jobLogs[currentPosition]);
    //
    // this.logs.splice(insertPosition + 1, 0, log);

    this.logs.push(log);

    this.logs.sort((a, b) => {
      return a.Timestamp - b.Timestamp || a.Id - b.Id;
    });

    const oversize = this.logs.length - this.logsMaxSize;
    if (oversize > this.logsMaxSize * 0.1) {
      this.logs = this.logs.slice(oversize, this.logs.length);
    }
  }

  @Mutation
  public clearLogs() {
    this.logs = [];
  }

  public get Logs(): JobOutputMessage[] {
    return this.logs;
  }

  public get LogsMaxSize(): number {
    return this.logsMaxSize;
  }

  @Mutation
  public setLogSubscriptionId(id: number) {
    this.logSubscriptionId = id;
  }


  /* Packets */
  @Action({commit: 'setPacketSubscriptionId', rawError: true})
  public async subscribePackets() {
    return await this.eventsService.subscribe('lora.forwarder.packets', async (event: Event) => {
      try {
        this.context.commit('addPacket', LoraPacket.Serializer.parse(event.Data));
      } catch (error) {
        this.logger.error(`Error receiving packet`, error);
      }
    });
  }

  @Action({rawError: true})
  public async unsubscribePackets() {
    if (this.packetsSubscriptionId !== null) {
      await this.eventsService.unsubscribe(this.packetsSubscriptionId);
    }
  }

  @Mutation
  public setPackets(packets: LoraPacket[]) {
    this.packets = packets;
  }

  @Mutation
  public addPacket(packet: LoraPacket) {

    console.log('adding', packet);

    this.packets.push(packet);

    // this.packets.sort((a, b) => {
    //   return a.Timestamp - b.Timestamp || a.Id - b.Id;
    // });

    const oversize = this.packets.length - this.packetsMaxSize;
    if (oversize > this.packetsMaxSize) {
      this.packets = this.packets.slice(oversize, this.packets.length);
    }
  }

  @Mutation
  public clearPackets() {
    this.packets = [];
  }

  public get Packets(): LoraPacket[] {
    return this.packets;
  }

  @Mutation
  public setPacketSubscriptionId(id: number) {
    this.packetsSubscriptionId = id;
  }
}
